import { motion } from "framer-motion";
import { Play, Pause, ArrowUpRight } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";

const PLATFORM_ICON = {
  Spotify: "https://cdn.simpleicons.org/spotify/1DB954",
  Audiomack: "https://cdn.simpleicons.org/audiomack/FFA200",
  "Apple Music": "https://cdn.simpleicons.org/applemusic/FA243C",
};

function TrackRow({ track, index }) {
  const { current, isPlaying, play } = usePlayer();
  const active = current && current.id === track.id && isPlaying;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="group relative flex items-start gap-4 sm:gap-6 p-5 sm:p-6 bg-[#121216] border border-zinc-800 rounded-md hover:border-rose-600/50 hover:bg-[#16161c] transition-colors duration-300"
      data-testid={`track-card-${index}`}
    >
      <button
        onClick={() => play(track)}
        className={`shrink-0 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full transition-colors ${
          active ? "bg-rose-600 text-white" : "bg-zinc-800 text-white hover:bg-rose-600"
        }`}
        data-testid={`track-play-${index}`}
        aria-label={`Play ${track.title}`}
      >
        {active ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
      </button>

      <span className="font-mono text-xs text-zinc-600 w-6 hidden sm:block">
        {String(index + 1).padStart(2, "0")}
      </span>

      <div className="min-w-0 flex-1">
        <h3 className="font-display text-2xl sm:text-3xl uppercase text-white leading-none truncate" data-testid={`track-title-${index}`}>
          {track.title}
        </h3>
        <p className="mt-1 text-sm text-zinc-400 truncate">
          {track.artist} <span className="text-zinc-600">·</span> {track.genre}
        </p>

        {/* Streaming links — always visible, open in new tab */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {[
            { name: "Spotify", url: track.spotify_url },
            { name: "Audiomack", url: track.audiomack_url },
            { name: "Apple Music", url: track.apple_url },
          ]
            .filter((p) => p.url)
            .map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 bg-[#0c0c0f] border border-zinc-800 hover:border-rose-600/60 hover:bg-[#16161c] px-3 py-1.5 rounded-full transition-colors"
                data-testid={`track-${index}-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                aria-label={`Open ${track.title} on ${p.name}`}
              >
                {PLATFORM_ICON[p.name] && <img src={PLATFORM_ICON[p.name]} alt="" className="w-3.5 h-3.5" />}
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-300">{p.name}</span>
                <ArrowUpRight className="w-3 h-3 text-zinc-500" />
              </a>
            ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function SoundSection({ content }) {
  return (
    <section id="sound" className="relative z-10 bg-[#09090b] py-20 sm:py-28 lg:py-36" data-testid="sound-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-rose-500">Press Play</span>
            <h2 className="mt-4 font-anton uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-white" data-testid="sound-headline">
              The Sound<br />of Mr Zee
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 text-sm sm:text-base leading-relaxed">{content.music_subtext}</p>
        </div>

        <div className="space-y-4">
          {content.tracks.map((t, i) => (
            <TrackRow key={t.id} track={t} index={i} />
          ))}
        </div>

        {/* Platform links */}
        <div className="mt-14 pt-10 border-t border-zinc-800">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">Follow on your platform</p>
          <div className="flex flex-wrap gap-4">
            {content.platform_links.map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 bg-[#121216] border border-zinc-800 hover:border-zinc-600 px-6 py-4 rounded-md transition-colors"
                data-testid={`platform-${p.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                {PLATFORM_ICON[p.name] && (
                  <img src={PLATFORM_ICON[p.name]} alt={p.name} className="w-5 h-5" />
                )}
                <span className="font-mono text-xs uppercase tracking-wider text-zinc-200 group-hover:text-white">
                  {p.name}
                </span>
                <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-rose-500 transition-colors" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
